import React, { useEffect } from 'react'
import tw, { css } from 'twin.macro'
import { BLOCKS, MARKS } from '@contentful/rich-text-types'
import { documentToReactComponents } from '@contentful/rich-text-react-renderer'
import { onAddToCart, onProductClick } from '../utils/analytics'
import { navigate } from 'gatsby'
import useCart from '../../hooks/useCart'
import useTheme from '../../hooks/theme'

import {
	PageBodySection,
	PageBodyContainer,
	BodyHead,
	BodyText,
	ProductSection,
	ProductsContainer,
	ItemContainer,
	ItemImageContainer,
	DetailsContainer,
	ButtonsContainer,
	NullText,
	AddToCart,
	Details,
	ProductName,
	ProductPrice,
	ProductBlurb,
	ProdHeading1,
	ProdHeading2,
	ProdHeading3,
	ProdHeading4,
	ProdHeading5,
	ProdHeading6,
	Italic,
	Bold,
	UnorderedList,
	Text,
	ProductDetailsSection,
	ProductDetailsContainer,
	ProductDetail,
	PaddingItemHolder,
	Enquire,
	EnquireZa,
} from './styles'

const options = {
	renderNode: {
		[BLOCKS.HEADING_2]: (node, children) => <BodyHead>{children}</BodyHead>,
		[BLOCKS.PARAGRAPH]: (node, children) => <BodyText>{children}</BodyText>,
	},
}

const itemoptions = {
	renderMark: {
		[MARKS.BOLD]: text => <Bold>{text}</Bold>,
		[MARKS.ITALIC]: text => <Italic>{text}</Italic>,
	},
	renderNode: {
		[BLOCKS.HEADING_1]: (node, children) => (
			<ProdHeading1>{children}</ProdHeading1>
		),
		[BLOCKS.HEADING_2]: (node, children) => (
			<ProdHeading2>{children}</ProdHeading2>
		),
		[BLOCKS.HEADING_3]: (node, children) => (
			<ProdHeading3>{children}</ProdHeading3>
		),
		[BLOCKS.HEADING_4]: (node, children) => (
			<ProdHeading4>{children}</ProdHeading4>
		),
		[BLOCKS.HEADING_5]: (node, children) => (
			<ProdHeading5>{children}</ProdHeading5>
		),
		[BLOCKS.HEADING_6]: (node, children) => (
			<ProdHeading6>{children}</ProdHeading6>
		),
		[BLOCKS.PARAGRAPH]: (node, children) => <Text>{children}</Text>,
		[BLOCKS.UL_LIST]: (node, children) => (
			<UnorderedList>{children}</UnorderedList>
		),
	},
}

const Products = ({ body, products, productdetails, checkedLocale, locale }) => {
	const { addToCart } = useCart()
	const { openCart } = useTheme()
	const [first, second] = productdetails

	const isBrowser = typeof window !== 'undefined'
	useEffect(() => {
		/**
		 * Google Analytics - Tag Manager - Enhanced Ecommerce
		 * Measuring product impressions for every search result change
		 * */
		const floatPrice = price =>
			price >= 1 ? parseFloat(price).toFixed(2) : parseFloat(0).toFixed(2)
		let impressions = products.map((product, index) => {
			const item = product.node
			return {
				name: item.seoTitle, // the name of the product, including Title and Subtitle
				id: item.sku, // product serial number
				price: Number(floatPrice(item.price)), // actual price per unit or in case of fixed price discounts the discounted price
				brand: item.brand, // product brand
				category: [item.categories && item.categories.title].toString(), // Dolphin domestic, Dolphin commercial, mineral swim, ozone swim
				list: 'Category List', // the type of list for the current product impression )e.g. Category List, Search Results etc.)
				position: index + 1, // the position of the product within the list
			}
		})
		if (isBrowser && window.dataLayer && products.length >= 1) {
			window.dataLayer.push({
				event: 'productImpression',
				ecommerce: {
					currencyCode: 'AUD',
					impressions,
				},
			})
		}
	}, [isBrowser, products])
	return (
		<>
			<PageBodySection>
				<PageBodyContainer>
					{documentToReactComponents(body.json, options)}
				</PageBodyContainer>
			</PageBodySection>

			{first ? (
				<ProductDetailsSection>
					<ProductDetailsContainer>
						<ProductDetail>
							<ProdHeading3>{first?.node.seoTitle}</ProdHeading3>
							{documentToReactComponents(first?.node.body.json, itemoptions)}
						</ProductDetail>
					</ProductDetailsContainer>
				</ProductDetailsSection>
			) : (
				''
			)}

			<ProductSection>
				<ProductsContainer>
					{products.map((x, i) => {
						return (
							<PaddingItemHolder key={i}>
								<ItemContainer>
									<ItemImageContainer
										css={{
											backgroundColor: x?.node?.images ? '' : '#8795a1',
										}}
										onClick={() => {
											onProductClick(
												{
													name: x.node.seoTitle,
													sku: x.node.sku,
													price: Number(x.node.price * 100),
													brand: x.node.brand,
													categories: x.node.categories,
													list: 'Category List',
													position: i + 1,
												},
												'Category List',
												i,
												navigate(`${checkedLocale}/product/${x.node.slug}`)
											)
										}}
									>
										{x?.node?.images ? (
											<picture>
												<source
													srcSet={x?.node?.images[0].fluid.srcWebp}
													type="image/webp"
												></source>
												<source
													srcSet={`${
														x?.node?.images[0].fluid.src.split('?')[0]
													}?fm=png&fl=png8&fit=pad&w=500&h=500`}
												></source>
												<img
													src={x?.node?.images[0].fluid.src}
													alt={x?.node?.images[0].description}
												/>
											</picture>
										) : (
											// <img
											// 	src={x?.node?.images[0].fluid.srcWebp}
											// 	alt={x?.node?.images[0].description}
											// />
											<NullText>No image</NullText>
										)}
									</ItemImageContainer>

									<DetailsContainer>
										<ProductName>{x?.node?.seoTitle}</ProductName>
										{products.active ? (
											<ProductPrice>
												{x?.node?.price.toLocaleString('en-AU', {
													style: 'currency',
													currency: 'AUD',
												})}
											</ProductPrice>
										) : (
											''
										)}
										<ProductBlurb>{x?.node?.seoDescription}</ProductBlurb>
									</DetailsContainer>
									{locale !== 'en-ZA' ? (
										<ButtonsContainer>
										{products.active ? (
											<AddToCart
												onClick={() => {
													addToCart({
														img:
															x.node.images != null
																? x.node.images[0].fluid.src
																: '',
														slug: x.node.slug,
														name: x.node.seoTitle,
														sku: x.node.sku,
														price: Number(x.node.price * 100),
														brand: x.node.brand,
														category: [
															x.node.categories && x.node.categories.title,
														].toString(),
														shippingClass: x.node.shippingClass,
														quantity: 1,
													})
													onAddToCart(
														{
															name: x.node.seoTitle,
															sku: x.node.sku,
															price: Number(x.node.price * 100),
															brand: x.node.brand,
															category: [
																x.node.categories && x.node.categories.title,
															].toString(),
															shippingClass: x.node.shippingClass,
															list: 'Category List',
															position: i + 1,
															quantity: 1,
														},
														'Category List'
													)
													openCart()
												}}
											>
												Add To Cart
											</AddToCart>
										) : (
											<Enquire
												to={`${checkedLocale}/support/contact/?subject=${encodeURIComponent(
													x?.node?.seoTitle.toLowerCase()
												)}#contact-form`}
											>
												Enquire Now
											</Enquire>
										)}
										<Details
											onClick={() => {
												onProductClick(
													{
														name: x.node.seoTitle,
														sku: x.node.sku,
														price: Number(x.node.price * 100),
														brand: x.node.brand,
														categories: x.node.categories,
														list: 'Category List',
														position: i + 1,
													},
													'Category List',
													i,
													navigate(`${checkedLocale}/product/${x.node.slug}`)
												)
											}}
										>
											Details
										</Details>
									</ButtonsContainer>
									) : (
										<ButtonsContainer>
											<EnquireZa
												to={`https://mydolphin.co.za/contact-us/`}
											>
												Enquire Now
											</EnquireZa>
										</ButtonsContainer>
									)}
									
								</ItemContainer>
							</PaddingItemHolder>
						)
					})}
				</ProductsContainer>
			</ProductSection>

			{second ? (
				<ProductDetailsSection>
					<ProductDetailsContainer>
						<ProductDetail>
							<ProdHeading3>{second?.node.seoTitle}</ProdHeading3>
							{documentToReactComponents(second?.node.body.json, itemoptions)}
						</ProductDetail>
					</ProductDetailsContainer>
				</ProductDetailsSection>
			) : (
				''
			)}
		</>
	)
}

export default Products
